import { useEffect, useState } from "react";
import { CopyIcon as Copy, XIcon as X, CircleNotchIcon as Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getImportBatch } from "@/lib/api";
import { batchStatusClass, batchStatusLabel, fmtDateTime, isLegacy } from "@/lib/provenanceDisplay";

/** Audit detail for one import batch — full SHA-256, affected tables, lineage. */
export default function ImportBatchDetail({ workspaceId, batchId, onClose, onJump }) {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    getImportBatch(workspaceId, batchId)
      .then(setBatch)
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load import batch."))
      .finally(() => setLoading(false));
  }, [workspaceId, batchId]);

  if (!batchId) return null;
  const legacy = isLegacy(batch);

  return (
    <aside className="ml-card p-5 space-y-4 lg:sticky lg:top-24" data-testid="import-batch-detail">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Import batch</div>
          <div className="ml-mono text-xs break-all">{batchId}</div>
        </div>
        <button onClick={onClose} className="ml-btn-ghost p-1" aria-label="Close" data-testid="import-batch-detail-close">
          <X size={14} />
        </button>
      </div>

      {loading || !batch ? (
        <div className="text-sm text-[var(--ml-text-secondary)] flex items-center gap-2">
          <Spinner size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <span className={`inline-block ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${batchStatusClass(batch.status)}`}>
            {batchStatusLabel(batch.status)}
          </span>
          <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 gap-x-3 text-xs">
            <Row k="Command" v={batch.source_command} mono />
            <Row k="Feeds" v={batch.entity} />
            <Row k="Channel" v={batch.ingest_channel} />
            <Row k="File" v={batch.original_filename || (legacy ? "Unknown" : "—")} />
            <Row k="Size" v={batch.file_size_bytes != null ? `${batch.file_size_bytes.toLocaleString()} B` : legacy ? "Unknown" : "—"} />
            <Row k="Imported" v={fmtDateTime(batch.imported_at)} />
            <Row k="Captured" v={legacy ? "Unknown" : fmtDateTime(batch.captured_at)} />
            <Row k="Imported by" v={batch.imported_by || (legacy ? "Unknown" : "—")} />
            <Row k="Rows read" v={batch.row_count} />
            <Row k="Imported" v={batch.imported_row_count} />
            <Row k="Skipped" v={batch.skipped_row_count} />
            <Row k="Parser" v={batch.parser_version || (legacy ? "Unknown" : "—")} mono />
          </dl>

          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">SHA-256</div>
            {batch.file_sha256 ? (
              <div className="flex items-start gap-2">
                <code className="ml-mono text-[11px] break-all leading-snug" data-testid="import-batch-sha256">{batch.file_sha256}</code>
                <button
                  className="ml-btn-ghost p-1 shrink-0"
                  title="Copy SHA-256"
                  data-testid="copy-sha256-btn"
                  onClick={() => navigator.clipboard?.writeText(batch.file_sha256).then(() => toast.success("SHA-256 copied."))}
                >
                  <Copy size={13} />
                </button>
              </div>
            ) : (
              <div className="text-xs text-[var(--ml-text-secondary)]">Unknown — no file hash exists for legacy data.</div>
            )}
          </div>

          <div>
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Affected tables (live rows)</div>
            {batch.affected_tables?.length ? (
              <ul className="text-xs space-y-0.5" data-testid="import-batch-affected">
                {batch.affected_tables.map((t) => (
                  <li key={t.table} className="flex justify-between gap-3">
                    <span className="ml-mono">{t.table}</span>
                    <span className="tabular-nums">{t.live_row_count.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-[var(--ml-text-secondary)]">No rows currently attributed (skipped, failed, or since cleared).</div>
            )}
          </div>

          {batch.duplicate_of && (
            <div className="text-xs">
              <span className="text-[var(--ml-text-secondary)]">Duplicate of </span>
              <button className="text-[var(--ml-primary)] hover:underline ml-mono" onClick={() => onJump(batch.duplicate_of.id)} data-testid="jump-duplicate-of">
                {batch.duplicate_of.id?.slice(0, 8)}…
              </button>
              <span className="text-[var(--ml-text-secondary)]"> ({fmtDateTime(batch.duplicate_of.imported_at)})</span>
            </div>
          )}
          {batch.duplicate_attempts > 0 && (
            <div className="text-xs text-[var(--ml-text-secondary)]">
              {batch.duplicate_attempts} later upload{batch.duplicate_attempts === 1 ? "" : "s"} of this file were skipped as duplicates.
            </div>
          )}
          {(batch.notes || batch.error_message) && (
            <div className="text-xs leading-snug">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">Notes</div>
              {batch.error_message && <p className="text-[var(--ml-error)] mb-1">{batch.error_message}</p>}
              {batch.notes && <p className={legacy ? "text-[#6929c4]" : "text-[var(--ml-text-secondary)]"}>{batch.notes}</p>}
            </div>
          )}
        </>
      )}
    </aside>
  );
}

function Row({ k, v, mono }) {
  return (
    <>
      <dt className="text-[var(--ml-text-secondary)]">{k}</dt>
      <dd className={`break-all ${mono ? "ml-mono" : ""}`}>{v ?? "—"}</dd>
    </>
  );
}
